import React from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import './AccessoriesPage.css'

interface Accessory {
  id: string;
  title: string;
  description: string;
  image: string;
  category: 'Performance' | 'Safety' | 'Convenience' | 'Maintenance';
}

const AccessoriesPage: React.FC = () => {
  const accessories: Accessory[] = [
    {
      id: 'aluminum-wheels',
      title: 'Aluminum Wheels',
      description: 'Aluminum wheels give your trailer a sporty look and are available as an upgrade on all models.',
      image: '/images/accessories/aluminum-wheels.png',
      category: 'Performance'
    },
    {
      id: 'dual-wheel-tongue-jack',
      title: 'Dual Wheel Tongue Jack',
      description: 'For heavier loads and additional manageability, the dual wheel tongue jack will make unhitched maneuverability easier.',
      image: '/images/accessories/dual-wheel-tongue-jack.png',
      category: 'Convenience'
    },
    {
      id: 'electric-brakes',
      title: 'Electric over Hydraulic Brakes',
      description: 'If you\'re looking for a hydraulic brake alternative, this is a great upgrade option.',
      image: '/images/accessories/electric-brakes.png',
      category: 'Safety'
    },
    {
      id: 'disc-brakes',
      title: 'G5 Stainless Steel Brakes',
      description: 'For those looking for a disc brake upgrade they can get stainless steel brakes.',
      image: '/images/accessories/disc-brakes.png',
      category: 'Safety'
    },
    {
      id: 'keel-rollers',
      title: 'Keel Rollers',
      description: 'Suitable to brace the added weight of heavier boats.',
      image: '/images/accessories/keel-rollers.png',
      category: 'Performance'
    },
    {
      id: 'led-lights',
      title: 'LED Lights',
      description: 'LED lights offer a significant safety performance benefit when used in automotive signaling: when power is applied, they rise to full intensity about 200 milliseconds faster than incandescent bulbs. This fast rise time makes the signal more readily visible, providing drivers following behind with additional time to react.',
      image: '/images/accessories/led-lights.png',
      category: 'Safety'
    },
    {
      id: 'poly-bunk-covers',
      title: 'Poly Bunk Covers',
      description: 'This polyurethane material covers the bunks and allows the boat to easily slide on and off the trailer.',
      image: '/images/accessories/poly-bunk-covers.png',
      category: 'Performance'
    },
    {
      id: 'pvc-load-guides',
      title: 'PVC Load Guides',
      description: 'Load guides are an optional accessory that make it easier to center the boat on the trailer during loading. They are particularly helpful if you do a lot of launching or loading in windy conditions or river currents.',
      image: '/images/accessories/pvc-load-guides.png',
      category: 'Convenience'
    },
    {
      id: 'spare-tire',
      title: 'Spare Tires',
      description: 'Spare tires are a necessity for all trailer owners and come in a variety of tire and wheel assemblies.',
      image: '/images/accessories/spare-tire.png',
      category: 'Safety'
    },
    {
      id: 'spare-tire-bracket',
      title: 'Spare Tire Brackets',
      description: 'We offer three different bracket sizes that fit from a 2×3 to a 3×5 frame size.',
      image: '/images/accessories/spare-tire-bracket.png',
      category: 'Convenience'
    },
    {
      id: 'stainless-steel-hardware',
      title: 'Stainless Steel Hardware',
      description: 'For those looking for a premium feature, upgrade to stainless steel hardware as it provides longer salt spray testing, making it superior in corrosion resistance.',
      image: '/images/accessories/stainless-steel-hardware.png',
      category: 'Performance'
    },
    {
      id: 'swing-tongue',
      title: 'Swing Tongue',
      description: 'A swing tongue allows the front of the trailer (the tongue) to swing backward, which decreases the overall length of the trailer. This allows it to fit in small spaces such as a garage.',
      image: '/images/accessories/swing-tongue.png',
      category: 'Convenience'
    },
    {
      id: 'target-bunks',
      title: 'Target Bunks',
      description: 'Additional set of bunks located in the front of the trailer to assist with loading and launching and to protect the keel of your boat.',
      image: '/images/accessories/target-bunks.png',
      category: 'Performance'
    },
    {
      id: 'torsion-axles',
      title: 'Torsion Axles',
      description: 'For those looking for an alternative suspension option, torsion axles require less maintenance.',
      image: '/images/accessories/torsion-axles.png',
      category: 'Performance'
    },
    {
      id: 'optional-tongue-lengths',
      title: 'Optional Tongue Lengths',
      description: 'We have a variety of tongue lengths available to get a better fit for your boat and trailer.',
      image: '/images/accessories/optional-tongue-lengths.png',
      category: 'Convenience'
    },
    {
      id: 'tongue-jacks',
      title: 'Tongue Jacks',
      description: 'Attaches to the trailer tongue, making it much easier to move the trailer around when it is not attached to the tow vehicle.',
      image: '/images/accessories/tongue-jacks.png',
      category: 'Convenience'
    },
    {
      id: 'windlass-bow-stop',
      title: 'Windlass Bow Stop',
      description: 'Useful as a forward bow stop for boats with a bow pulpit or windlass bow anchor that could interfere with the normal operation of the winch stand.',
      image: '/images/accessories/windlass-bow-stop.png',
      category: 'Performance'
    }
  ];


  const accessoryFeatures = [
    "Premium quality materials and construction",
    "Professional installation available",
    "Compatible with all Venture trailer models",
    "Backed by manufacturer warranty",
    "Designed for marine environments",
    "Easy maintenance and long-lasting durability"
  ];

  return (
    <div className="accessories-page" id="accessories">
      <SEOHead
        title="Boat Trailer Accessories & Upgrades | Fast Boat Trailers"
        description="Boat trailer accessories and upgrades from Fast Boat Trailers in Edgewater, MD. Aluminum wheels, electric brakes, spare tire carriers, guide posts, LED lights, and more performance and safety upgrades."
        keywords="trailer accessories, boat trailer upgrades, aluminum wheels, electric brakes, spare tire carrier, trailer lights, guide posts, trailer jacks"
        ogTitle="Boat Trailer Accessories & Upgrades"
        ogDescription="Boat trailer accessories and upgrades - aluminum wheels, electric brakes, spare tire carriers, LED lights, and more."
        ogImage="/images/accessories/aluminum-wheels.png"
        canonical="https://fastboattrailers.com/trailers/accessories"
      />
      <div className="container">
        {/* Hero Section */}
        <div className="accessories-hero">
          <div className="hero-main">
            <div className="hero-content-left">
              <h1>Boat Trailer Accessories & Upgrades</h1>
              <h2>Give Your Trailer the Upgrade It Deserves</h2>
              <p>
                Our boat trailer upgrades are designed to improve your trailer's capabilities and dependability,
                making your boating experience even more enjoyable. Our team can help you design your trailer
                and have it custom-built to suit your watercraft needs.
              </p>
            </div>
            <div className="hero-content-right">
              <h3>Why Choose Venture Accessories?</h3>
              <ul className="benefits-list">
                {accessoryFeatures.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Accessories Grid */}
        <div className="accessories-grid">
          {accessories.map((accessory) => (
            <div key={accessory.id} className="accessory-card">
              <div className="accessory-image">
                <img src={accessory.image} alt={accessory.title} />
              </div>
              <div className="accessory-content">
                <h3>{accessory.title}</h3>
                <p>{accessory.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="accessories-cta">
          <h3>Ready to Upgrade Your Trailer?</h3>
          <p>
            Contact Fast Boat Trailers today to discuss your accessory needs and get expert recommendations 
            for enhancing your trailer's performance and functionality.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-large">Get Custom Quote</Link>
            <Link to="/tips" className="btn btn-secondary">Installation Tips</Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccessoriesPage